import { DataGrid } from "@mui/x-data-grid";
import { Box, Paper, Typography, CircularProgress } from "@mui/material";
import { IncomeSummaryProps } from "../../../helpers/types";
import { useStrategyQuery } from "hooks/useStrategyQuery";
import { useAccountQuery } from "hooks/useAccountQuery";
import { useStatisticGrid } from "../../../hooks/useStatisticGrid";
import { useGetReportQuery } from "../../../features/trading/accounts";
import { useReportTrigger } from "../../../containers/ReportPageDeprecated";
import { useMemo } from "react";
import { asCurrency } from "../../../components/ui/formatters";

const IncomeSummary = ({ columnPeriods }: IncomeSummaryProps) => {
  const { data: stockData } = useStrategyQuery();
  const { data: accountData } = useAccountQuery();
  const { shouldFetch } = useReportTrigger();
  const { gridColumns, buildRow, gridStyles } = useStatisticGrid({
    columnPeriods,
    stockName: stockData?.name,
  });

  // Create query parameters
  const queryParams = useMemo(() => {
    if (stockData?.id && accountData?.id && shouldFetch) {
      return {
        account_ids: [accountData.id],
        strategy_ids: [stockData.id],
        periods: columnPeriods.map((col) => ({
          name: col.name,
          period: col.period,
        })),
      };
    }
    return null;
  }, [stockData?.id, accountData?.id, columnPeriods, shouldFetch]);

  // Use the query hook
  const {
    data: reportData,
    isLoading,
    error,
  } = useGetReportQuery(queryParams!, {
    skip: !queryParams,
  });

  // Memoize the rows calculation
  const rows = useMemo(() => {
    if (reportData?.data?.income_summary) {
      const incomeSummary = reportData.data.income_summary;

      return [
        buildRow(
          "Realized Option G/L",
          columnPeriods.map((period) => {
            const periodData = incomeSummary.find(
              (item: any) => item.name === period.name,
            );
            return periodData
              ? asCurrency(periodData.value.value_change)
              : "$0.00";
          }),
          1,
        ),
        buildRow(
          "Current unrealized G/L",
          columnPeriods.map((period, index) => {
            const periodData = incomeSummary.find(
              (item: any) => item.name === period.name,
            );
            // return periodData ? asCurrency(periodData.value.beginning_value) : '$0.00';
            if (index === 0) {
              return "Calculating...";
            } else {
              // return periodData ? asCurrency(periodData.value.value_change) : '$0.00';
              return "";
            }
          }),
          2,
        ),
        buildRow(
          "Cashflow",
          columnPeriods.map((period) => {
            const periodData = incomeSummary.find(
              (item: any) => item.name === period.name,
            );
            return periodData
              ? asCurrency(periodData.value.cashflows)
              : "$0.00";
          }),
          3,
        ),
        buildRow(
          "Dividend income",
          columnPeriods.map((period) => {
            const periodData = incomeSummary.find(
              (item: any) => item.name === period.name,
            );
            return periodData
              ? asCurrency(periodData.value.dividends)
              : "$0.00";
          }),
          4,
        ),
      ];
    }
    return [];
  }, [reportData?.data?.income_summary, columnPeriods, buildRow]);

  if (isLoading) {
    return (
      <Box
        sx={{
          mt: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Income Summary
        </Typography>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography color="error">
            Error loading report data. Please try again.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Income Summary
      </Typography>
      <Paper elevation={1}>
        <DataGrid
          autoHeight
          rows={rows}
          columns={gridColumns}
          hideFooter
          disableColumnMenu
          disableRowSelectionOnClick
          disableColumnFilter
          sx={gridStyles}
        />
      </Paper>
    </Box>
  );
};

export default IncomeSummary;
